import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Checkbox, Drawer, Empty, Input, Segmented, Space, Spin, Tooltip, Typography, message } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined, StarOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  decideProposals,
  describeCost,
  getGeneration,
  isGenerationFinished,
  listProposals,
  type ProposalDecision,
  type ScenarioGeneration,
  type ScenarioProposal,
} from '@/api/scenarioIntelligence';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { usePollGeneration } from './generation';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// ProposalsDrawer — Scenario Intelligence SI-05a, mockup 03 (accepted 1 Nov 2026).
// What Generate returns, as cards a person can judge: the seven fields (Situation, Why it matters, Expected behavior,
// Evidence needed, Source, What makes this case distinct, Review state); paraphrases grouped under their case and counted
// once; "Add to library as draft" and "Reject", per card and as a batch of eligible items that names what it leaves out.
// Adding never approves: approval is a separate step in Reviews. An incomplete draft names its gap and cannot run.
// The AI-action bar is drawn because the screen was accepted with it, but only actions with a real backend are enabled:
// the nine actions and "Ask for a change" arrive with SI-05b, and say so.
// ─────────────────────────────────────────────────────────────────────────────

export const AI_ACTIONS = [
  'Find missing cases',
  'Make this more realistic',
  'Add a boundary case',
  'Add a failure case',
  'Personalize',
  'Check the expected behavior',
  'Strengthen the checks',
  'Simplify wording',
  'Remove duplicates',
] as const;

const LATER = 'Arrives with SI-05b: AI improvements need the improvement workflow, which is not built yet.';

export const ProposalsDrawer = observer(function ProposalsDrawer({
  open,
  onClose,
  agentId,
  generationKey,
  onGenerateMore,
}: {
  open: boolean;
  onClose: () => void;
  agentId: number | null;
  /** Open on this generation (e.g. the one just started); otherwise the newest. */
  generationKey?: string | null;
  onGenerateMore?: () => void;
}) {
  const { themeStore, merchantContextStore: ctx } = useStore();
  const isDark = themeStore.mode === 'dark';
  const merchantId = ctx.selectedMerchantId;

  const [sets, setSets] = useState<ScenarioGeneration[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(generationKey ?? null);
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [refusals, setRefusals] = useState<string[]>([]);
  const seq = useRef(0);

  const load = useCallback(async (focusKey?: string | null) => {
    if (agentId == null) return;
    const mine = ++seq.current;
    setError(null);
    try {
      let list = await listProposals(agentId, merchantId);
      // A generation still drafting (or one that kept nothing) has no proposal row yet: read it by its key.
      if (focusKey && !list.some((g) => g.key === focusKey)) {
        const one = await getGeneration(focusKey, merchantId).catch(() => null);
        if (one) list = [one, ...list];
      }
      if (mine !== seq.current) return;
      setSets(list);
      setSelectedKey((k) => (focusKey && list.some((g) => g.key === focusKey) ? focusKey : k && list.some((g) => g.key === k) ? k : list[0]?.key ?? null));
    } catch (e) {
      if (mine === seq.current) setError((e as Error).message);
    }
  }, [agentId, merchantId]);

  useEffect(() => {
    if (!open) return;
    setSets(null);
    setSelection(new Set());
    setRefusals([]);
    setSelectedKey(generationKey ?? null);
    void load(generationKey);
  }, [open, generationKey, load]);

  const selected = sets?.find((g) => g.key === selectedKey) ?? null;
  // A generation still drafting is followed until it finishes; each read replaces it in the list, so the list stays the
  // one source of what is shown — a decision's fresh read is never overwritten by an older poll.
  const live = usePollGeneration(selected && !isGenerationFinished(selected) ? selected.key : null, merchantId, selected);
  useEffect(() => {
    if (live) setSets((list) => (list && list.some((g) => g.key === live.key && g !== live) ? list.map((g) => (g.key === live.key ? live : g)) : list));
  }, [live]);
  const generation = selected;
  const narrow = useNarrow();

  const proposals = generation?.proposals ?? [];
  const open_ = proposals.filter((p) => p.state === 'Proposed');
  const chosen = open_.filter((p) => selection.has(p.draftId));
  const eligible = chosen.filter((p) => p.batchEligible);
  const excluded = chosen.filter((p) => !p.batchEligible);

  const decide = async (ids: number[], decision: ProposalDecision, batch: boolean) => {
    setBusy(`${decision}:${ids.join(',')}`);
    setRefusals([]);
    try {
      const outcomes = await decideProposals(ids, decision, { batch, merchantId });
      const done = outcomes.filter((o) => o.outcome === 'Added' || o.outcome === 'Rejected');
      const refused = outcomes.filter((o) => o.outcome === 'Refused');
      if (done.length) {
        void message.success(
          decision === 'Add'
            ? `${done.length} added to the library as draft${done.length === 1 ? '' : 's'}. Each waits for its scenario approval in Reviews.`
            : `${done.length} rejected.`,
        );
      }
      setRefusals(refused.map((o) => `${proposals.find((p) => p.draftId === o.draftId)?.card?.title ?? `Draft #${o.draftId}`}: ${o.reason}`));
      setSelection(new Set());
      if (generation) {
        const fresh = await getGeneration(generation.key, merchantId);
        setSets((list) => (list ?? []).map((g) => (g.key === fresh.key ? fresh : g)));
      }
    } catch (e) {
      setRefusals([(e as Error).message]);
    } finally {
      setBusy(null);
    }
  };

  const toggle = (id: number, on: boolean) =>
    setSelection((s) => {
      const next = new Set(s);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const bodyBg = isDark ? colors.slate[900] : colors.slate[50];
  const panelBg = isDark ? colors.slate[800] : '#FFFFFF';
  const border = isDark ? colors.slate[700] : colors.slate[200];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      size="min(1040px, 100vw)"
      destroyOnHidden
      title={
        <div>
          <span>Proposals</span>
          <Text type="secondary" style={{ display: 'block', fontSize: 12, fontWeight: 400 }}>
            {generation?.agentCanonicalName
              ? `${generation.agentCanonicalName} · ${generation.agentName ?? ''} · #${generation.agentId}`
              : 'Generated scenarios, as drafts. Adding one never approves it.'}
          </Text>
        </div>
      }
      extra={
        onGenerateMore ? (
          <Button icon={<StarOutlined />} onClick={onGenerateMore} aria-label="Generate more" title="Generate more">
            {narrow ? null : 'Generate more'}
          </Button>
        ) : undefined
      }
      styles={{ body: { padding: 16, background: bodyBg } }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {agentId == null && <Alert type="info" showIcon title="Choose an agent in the filter bar to see its proposals." />}
      {error && <Alert type="error" showIcon title="Could not load proposals" description={error} />}
      {agentId != null && !sets && !error && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spin />
        </div>
      )}
      {sets && sets.length === 0 && (
        <div style={{ background: panelBg, border: `1px solid ${border}`, borderRadius: 10, padding: 24 }}>
          <Empty description="No proposals yet for this agent in this practice. Generate scenarios to get a proposed set." />
        </div>
      )}

      {sets && sets.length > 1 && (
        <Segmented
          size="small"
          value={selectedKey ?? undefined}
          onChange={(v) => {
            setSelectedKey(String(v));
            setSelection(new Set());
          }}
          options={sets.map((g) => ({ value: g.key, label: `${dayjs(g.requestedAtUtc).format('D MMM HH:mm')} · ${g.proposalCount}` }))}
        />
      )}

      {generation && (
        <>
          <SetHeader generation={generation} isDark={isDark} />
          <AiActionsBar selectedCount={chosen.length} isDark={isDark} />

          {refusals.length > 0 && (
            <Alert
              type="warning"
              showIcon
              title="Not everything was done"
              description={refusals.map((r) => <div key={r}>{r}</div>)}
              closable
              onClose={() => setRefusals([])}
            />
          )}

          {generation.state === 'Failed' && (
            <Alert
              type="error"
              showIcon
              title="This generation failed: nothing was saved as a proposal"
              description={
                <>
                  <div>{generation.stateReason}</div>
                  {generation.failedAttemptReasons.map((r) => (
                    <div key={r} style={{ fontSize: 12, marginTop: 4 }}>{`${r} Kept on its model-call record; never a proposal.`}</div>
                  ))}
                  <div style={{ fontSize: 12, marginTop: 4 }}>Retrying never creates a second copy of a proposal that already exists.</div>
                </>
              }
              action={onGenerateMore ? <Button size="small" onClick={onGenerateMore}>Generate again</Button> : undefined}
            />
          )}
          {!isGenerationFinished(generation) && (
            <div style={{ background: panelBg, border: `1px solid ${border}`, borderRadius: 10, padding: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
              <Spin />
              <div>
                <Text strong>Drafting…</Text>
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  {`Cards appear once each proposal is checked. You can leave; the job continues.${describeCost(generation.cost) ? ` ${describeCost(generation.cost)} so far.` : ''}`}
                </Text>
              </div>
            </div>
          )}
          {generation.state === 'Completed' && proposals.length === 0 && (
            <div style={{ background: panelBg, border: `1px solid ${border}`, borderRadius: 10, padding: 20 }}>
              <Text strong>No new proposals for this concern</Text>
              <Text type="secondary" style={{ display: 'block', fontSize: 12.5, marginTop: 4 }}>
                The Scenario model’s answer held nothing the rules could keep as a distinct case.
              </Text>
            </div>
          )}

          {open_.length > 0 && (
            <div style={{ background: panelBg, border: `1px solid ${border}`, borderRadius: 10, padding: '10px 14px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Checkbox
                checked={chosen.length > 0 && chosen.length === open_.length}
                indeterminate={chosen.length > 0 && chosen.length < open_.length}
                onChange={(e) => setSelection(e.target.checked ? new Set(open_.map((p) => p.draftId)) : new Set())}
              >
                {`${chosen.length} of ${open_.length} selected`}
              </Checkbox>
              <Text type="secondary" style={{ fontSize: 12.5, flex: 1, minWidth: 220 }}>
                {chosen.length === 0
                  ? 'Select proposals to add or reject them together. Only batch-eligible ones can be added together.'
                  : `Batch-eligible: ${eligible.length} can be added together` +
                    (excluded.length ? ` · ${excluded.length} excluded: ${excluded.map((p) => p.batchExclusion).join('; ')}` : '')}
              </Text>
              <Space wrap>
                <Button
                  icon={<CloseOutlined />}
                  disabled={chosen.length === 0 || busy != null}
                  loading={busy?.startsWith('Reject:') && chosen.length > 1}
                  onClick={() => void decide(chosen.map((p) => p.draftId), 'Reject', true)}
                >
                  {`Reject ${chosen.length || ''}`.trim()}
                </Button>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  disabled={eligible.length === 0 || busy != null}
                  loading={busy?.startsWith('Add:') && eligible.length > 1}
                  onClick={() => void decide(eligible.map((p) => p.draftId), 'Add', true)}
                >
                  {`Add ${eligible.length || ''} to library as draft${eligible.length === 1 ? '' : 's'}`.replace('  ', ' ')}
                </Button>
              </Space>
            </div>
          )}

          {proposals.map((p) => (
            <ProposalCardView
              key={p.draftId}
              proposal={p}
              isDark={isDark}
              selected={selection.has(p.draftId)}
              onSelect={(on) => toggle(p.draftId, on)}
              busy={busy}
              onDecide={(d) => void decide([p.draftId], d, false)}
            />
          ))}

          {generation.uncovered.length > 0 && (
            <div style={{ background: panelBg, border: `1px dashed ${border}`, borderRadius: 10, padding: '12px 16px' }}>
              <Text strong style={{ fontSize: 13 }}>{`${generation.uncovered.length} obligation${generation.uncovered.length === 1 ? '' : 's'} stay uncovered with a budget of ${generation.maxProposals}`}</Text>
              <ul style={{ margin: '6px 0 6px 18px', padding: 0, fontSize: 12.5 }}>
                {generation.uncovered.map((u) => <li key={u}>{u}</li>)}
              </ul>
              <Tooltip title={LATER}>
                <Button size="small" disabled icon={<StarOutlined />}>Find missing cases</Button>
              </Tooltip>
            </div>
          )}
        </>
      )}
      </div>
    </Drawer>
  );
});

function SetHeader({ generation, isDark }: { generation: ScenarioGeneration; isDark: boolean }) {
  const cost = describeCost(generation.cost);
  const model = generation.scenarioModel?.deploymentName ?? generation.scenarioModel?.modelName ?? 'the Scenario model';
  return (
    <div style={{ background: isDark ? colors.slate[800] : '#FFFFFF', border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`, borderRadius: 10, padding: '14px 16px' }}>
      <Text strong style={{ fontSize: 14.5 }}>{`Proposals · generated ${dayjs(generation.requestedAtUtc).format('D MMM HH:mm')} by ${model}`}</Text>
      <div style={{ fontSize: 13, marginTop: 4 }}>
        Concern: <i>{`“${generation.concern}”`}</i>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        <StatusPill tone="slate">{`${generation.proposalCount} proposal${generation.proposalCount === 1 ? '' : 's'}`}</StatusPill>
        <StatusPill tone="slate">{`${generation.familyCount} famil${generation.familyCount === 1 ? 'y' : 'ies'}`}</StatusPill>
        <StatusPill tone="slate">{`${generation.paraphrasesGrouped} paraphrase${generation.paraphrasesGrouped === 1 ? '' : 's'} grouped`}</StatusPill>
        {generation.incompleteCount > 0 && <StatusPill tone="amber">{`${generation.incompleteCount} incomplete · cannot run`}</StatusPill>}
        {generation.uncovered.length > 0 && (
          <StatusPill tone="amber">{`${generation.uncovered.length} still uncovered with a budget of ${generation.maxProposals}`}</StatusPill>
        )}
        <StatusPill tone={cost ? 'ink' : 'slate'}>{cost ? `Generation · ${cost}` : 'Generation cost not recorded yet'}</StatusPill>
      </div>
    </div>
  );
}

/** The violet bar of mockup 03: drawn as accepted, every action disabled until its backend exists (SI-05b). */
function AiActionsBar({ selectedCount, isDark }: { selectedCount: number; isDark: boolean }) {
  return (
    <div
      aria-label="AI actions"
      style={{
        background: isDark ? colors.violet[700] + '22' : colors.violet[50],
        border: `1px solid ${isDark ? colors.violet[700] : colors.violet[200]}`,
        borderRadius: 10,
        padding: '12px 14px',
      }}
    >
      <Text style={{ fontSize: 12.5, color: isDark ? colors.violet[200] : colors.violet[700] }}>
        {`✦ AI actions · act on the ${selectedCount ? `${selectedCount} selected card${selectedCount === 1 ? '' : 's'}` : 'whole set when none is selected'} — arrive with SI-05b`}
      </Text>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0' }}>
        {AI_ACTIONS.map((a) => (
          <Tooltip key={a} title={LATER}>
            <Button size="small" disabled>{a}</Button>
          </Tooltip>
        ))}
      </div>
      <Space.Compact style={{ width: '100%' }}>
        <Input disabled placeholder={'Ask for a change… e.g. "make the daughter the one who writes in" (arrives with SI-05b)'} />
        <Tooltip title={LATER}>
          <Button disabled>Preview</Button>
        </Tooltip>
      </Space.Compact>
    </div>
  );
}

function ProposalCardView({
  proposal: p,
  isDark,
  selected,
  onSelect,
  busy,
  onDecide,
}: {
  proposal: ScenarioProposal;
  isDark: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
  busy: string | null;
  onDecide: (d: ProposalDecision) => void;
}) {
  const [showParaphrases, setShowParaphrases] = useState(false);
  const card = p.card;
  const border = isDark ? colors.slate[700] : colors.slate[200];
  const panelBg = isDark ? colors.slate[800] : '#FFFFFF';
  const labelColor = isDark ? colors.slate[400] : colors.slate[500];
  const incomplete = p.readiness === 'Incomplete';
  const myBusy = busy?.endsWith(`:${p.draftId}`) ? busy.split(':')[0] : null;

  if (p.state === 'Rejected') {
    return (
      <div data-proposal={p.draftId} style={{ background: panelBg, border: `1px solid ${border}`, borderRadius: 10, padding: '10px 14px', opacity: 0.8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <StatusPill tone="slate">Rejected</StatusPill>
        <Text delete>{card?.title ?? p.scenarioId}</Text>
        {p.decidedAtUtc && <Text type="secondary" style={{ fontSize: 12 }}>{`· ${dayjs(p.decidedAtUtc).format('D MMM HH:mm')}`}</Text>}
      </div>
    );
  }
  if (!card) {
    return <Alert type="warning" showIcon title={`Draft #${p.draftId} cannot be read`} description="Its card could not be read; nothing was changed." />;
  }

  const reviewState =
    p.state === 'Added'
      ? `Added to the library as a draft${p.decidedAtUtc ? `, ${dayjs(p.decidedAtUtc).format('D MMM HH:mm')}` : ''}, as ${p.scenarioId}. Its scenario approval waits in Reviews; until then any run of it is Exploratory.`
      : incomplete
        ? `Proposed by ${p.modelName ?? 'the Scenario model'}. Not reviewed. An incomplete draft: it cannot run, not even as Exploratory, until its gap is resolved.`
        : `Proposed by ${p.modelName ?? 'the Scenario model'}. Not reviewed. A runnable draft: if run now its result is Exploratory and never counts toward release; approval later does not promote that result, a new run after approval does.`;

  return (
    <div data-proposal={p.draftId} style={{ background: panelBg, border: `1px solid ${selected ? colors.ink[500] : border}`, borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}` }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {p.state === 'Proposed' && <Checkbox checked={selected} onChange={(e) => onSelect(e.target.checked)} aria-label={`Select ${card.title}`} />}
          <Text style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: labelColor }}>{`Family · ${card.family}`}</Text>
          <span style={{ flex: 1 }} />
          <StatusPill tone={incomplete ? 'amber' : 'violet'}>{incomplete ? 'Incomplete draft · cannot run' : 'Runnable draft'}</StatusPill>
          <StatusPill tone={p.state === 'Added' ? 'ink' : 'slate'}>{p.state === 'Added' ? 'Added to library as draft · awaiting approval' : 'Proposed · not reviewed'}</StatusPill>
          <code style={{ fontSize: 11.5, color: labelColor }}>{`draft #${p.draftId}`}</code>
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, marginTop: 6 }}>{card.title}</div>
      </div>

      {incomplete && card.gaps.length > 0 && (
        <div style={{ padding: '10px 16px', background: isDark ? colors.amber[700] + '22' : colors.amber[100] + '88', borderBottom: `1px solid ${border}` }}>
          {card.gaps.map((g) => (
            <div key={g.kind + g.text} style={{ fontSize: 12.5, marginBottom: 4 }}>
              <WarningOutlined style={{ color: colors.amber[600], marginRight: 6 }} />
              <b>{g.text}</b> <span style={{ color: isDark ? colors.slate[300] : colors.slate[600] }}>{g.action}</span>
            </div>
          ))}
        </div>
      )}

      <Field label="Situation" isDark={isDark}>
        {card.situation && <div>{card.situation}</div>}
        {card.turns.map((t, i) => (
          <div key={i} style={{ margin: '4px 0 0', paddingLeft: 10, borderLeft: `2px solid ${border}`, fontStyle: 'italic' }}>
            {card.turns.length > 1 ? `Turn ${i + 1}: ` : ''}
            {`“${t}”`}
          </div>
        ))}
      </Field>
      <Field label="Why it matters" isDark={isDark}>{card.whyItMatters || <Text type="secondary">Not said.</Text>}</Field>
      <Field label="Expected behavior" isDark={isDark}>
        {card.expectedBehavior.length === 0 ? (
          <Text type="secondary">{incomplete ? 'Not written: it waits for what the gap above names.' : 'None.'}</Text>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {card.expectedBehavior.map((e, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                <span>{e.text}</span>{' '}
                {e.pendingSmeReview ? <StatusPill tone="amber" size="sm">Pending SME review</StatusPill> : <StatusPill tone="slate" size="sm">Operational</StatusPill>}
              </li>
            ))}
          </ul>
        )}
      </Field>
      <Field label="Evidence needed" isDark={isDark}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[...card.expectedBehavior.flatMap((e) => e.checks), ...card.evidenceNeeded]
            .filter((v, i, all) => all.indexOf(v) === i)
            .map((c) => (
              <code key={c} style={{ fontSize: 11.5, padding: '1px 8px', borderRadius: 6, border: `1px solid ${border}`, background: isDark ? colors.slate[900] : colors.slate[50] }}>{c}</code>
            ))}
        </div>
      </Field>
      <Field label="Source" isDark={isDark}>
        {card.sources.length === 0 ? (
          <Text type="secondary">None named.</Text>
        ) : (
          <span>
            {card.requirement && <span>{`Proposed requirement (not reviewed): ${card.requirement} · `}</span>}
            {card.sources.map((s, i) => (
              <span key={s.kind + s.ref}>
                {i > 0 ? ' · ' : ''}
                {s.kind === 'Instructions' ? 'the agent’s instructions' : (
                  <>
                    {`${s.kind.toLowerCase()} `}
                    <code style={{ fontSize: 12 }}>{s.ref}</code>
                  </>
                )}
                {s.note ? `, ${s.note}` : ''}
                {!s.verified && <StatusPill tone="red" size="sm">not configured for this agent</StatusPill>}
              </span>
            ))}
          </span>
        )}
      </Field>
      <Field label="What makes this case distinct" isDark={isDark}>
        {card.distinct || <Text type="secondary">The Scenario model did not say.</Text>}
        {card.possibleDuplicateOf && (
          <div style={{ marginTop: 4 }}>
            <StatusPill tone="amber" size="sm">{`Possible duplicate of ${card.possibleDuplicateOf}`}</StatusPill>
          </div>
        )}
      </Field>
      <Field label="Review state" isDark={isDark} last={card.paraphrases.length === 0 && card.problems.length === 0}>
        {reviewState}
      </Field>

      {(card.paraphrases.length > 0 || card.problems.length > 0) && (
        <div style={{ padding: '8px 16px', borderTop: `1px solid ${border}` }}>
          {card.paraphrases.length > 0 && (
            <Button type="link" size="small" style={{ paddingInline: 0 }} onClick={() => setShowParaphrases((v) => !v)}>
              {`${showParaphrases ? '▾' : '▸'} ${card.paraphrases.length} paraphrase${card.paraphrases.length === 1 ? '' : 's'} grouped: count as 1 case`}
            </Button>
          )}
          {showParaphrases && card.paraphrases.map((t) => (
            <div key={t} style={{ fontStyle: 'italic', fontSize: 12.5, margin: '2px 0 2px 12px' }}>{`“${t}”`}</div>
          ))}
          {card.problems.length > 0 && (
            <details style={{ fontSize: 12, marginTop: 4, color: isDark ? colors.slate[300] : colors.slate[600] }}>
              <summary>{`What the rules changed or flagged (${card.problems.length})`}</summary>
              <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                {card.problems.map((x) => <li key={x}>{x}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}

      {p.state === 'Proposed' && (
        <div style={{ padding: '10px 16px', borderTop: `1px solid ${border}`, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Tooltip title={incomplete ? 'An incomplete draft cannot join the library yet: the library runs every active scenario, and this one has no expected behavior a run could judge.' : undefined}>
            <Button type="primary" icon={<CheckOutlined />} disabled={incomplete || busy != null} loading={myBusy === 'Add'} onClick={() => onDecide('Add')}>
              Add to library as draft
            </Button>
          </Tooltip>
          <Button icon={<CloseOutlined />} disabled={busy != null} loading={myBusy === 'Reject'} onClick={() => onDecide('Reject')}>
            Reject
          </Button>
          <Tooltip title="Editing a proposal arrives with the advanced editor (SI-06a). Add it, then edit it in the library.">
            <Button icon={<EditOutlined />} disabled>Edit</Button>
          </Tooltip>
          <Tooltip title={LATER}>
            <Button icon={<StarOutlined />} disabled>AI actions</Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, isDark, last }: { label: string; children: React.ReactNode; isDark: boolean; last?: boolean }) {
  const narrow = useNarrow();
  const border = isDark ? colors.slate[700] : colors.slate[200];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: narrow ? '1fr' : '180px 1fr',
        borderBottom: last ? 'none' : `1px solid ${border}`,
      }}
    >
      <div style={{ padding: narrow ? '10px 16px 0' : '10px 16px', fontSize: 12.5, fontWeight: 600, color: isDark ? colors.slate[400] : colors.slate[500], background: narrow ? 'transparent' : isDark ? colors.slate[900] + '66' : colors.slate[50] }}>
        {label}
      </div>
      <div style={{ padding: narrow ? '4px 16px 10px' : '10px 16px', fontSize: 13.5, overflowWrap: 'anywhere' }}>{children}</div>
    </div>
  );
}

/** Phone width: the card's label column folds above its value (mockup convention at 375 px). */
function useNarrow(): boolean {
  const query = '(max-width: 640px)';
  const get = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false);
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const m = window.matchMedia(query);
    const on = () => setNarrow(m.matches);
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, []);
  return narrow;
}

